import { z } from "zod"
import { BackButton } from "@/components/custom/BackButton";
import { PageTitle } from "@/components/custom/PageTitle";
import { AppDispatch, RootState } from "@/redux";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "@/components/ui/form"
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { WorkflowCreateUpdateSchemaV2, WorkflowStatus } from "@/constants/workflows/workflows";
import { Section } from "./components/Section";
import { TextInputField } from "@/components/custom/InputField/TextInputField";
import { WorkflowExecution } from "./components/WorkflowExecution";
import { getFormProps } from "@/helpers/formHelpers";
import { InputFieldWrapper } from "./components/InputFieldsSection";
import { useEffect, useState } from "react";
import { WorkflowInDetailV2 } from "@/types/WorkflowCreate/workflowDetailsV2";
import { getSingleWorkflow, updateWorkflow, updateDraftWorkflow } from "@/services/workflowService";
import { hasValidApplications, isValidActionsSubjectsAndCredentials } from "../helpers/formValidation";
import { workflowActions } from "@/redux/slice/workflowSlice";
import { TransformationStepType, WorkflowRunType, WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { convertFromUTC, convertToUTC, encodeCronMetadata, decodeCronMetadata, convertUTCToQuartzCron, convertQuartzCronToUTC } from "@/helpers/date";
import { StepTask } from "./components/wfStep/Step";
import { FieldMapping as FieldMappingType } from "@/types/WorkflowCreate/workflowCreate"
import { getFieldMappingsAndMetadataDifference, groupWorkflowFieldDiffs } from "./helpers/getFieldMappingsDifference";
import { getTransformationsDifference } from "./helpers/getTransformationsDifference";
import { SelectTaskApplication } from "./components/Modals/SelectTaskApplication";
import { Button } from "@/components/ui/button";
import { ButtonVariant } from "@/constants/button/button";
import { buildUpdatedWorkflowTasks } from "./helpers/getTaskDifference";
import { WorkflowTask } from "@/types/WorkflowCreate/workflowUpdateV2";
import { StepTransformation } from "./components/wfStep/Transformation";
import { Separator } from "@/components/ui/separator";
import { Settings } from "lucide-react";
import { ADD_CUSTOM_LABEL_FIELD_RESPONSE } from "@/constants/common";
import { buildTransformationSchema } from "@/pages/Workflows/components/TransformationComponents/helpers/fields";
import { FieldsResponse } from "@/types/WorkflowCreate/fields";

type TransformationConfigurationInput = Parameters<typeof buildTransformationSchema>[0];

const normalizeStepType = (stepType: string): WorkflowStepType =>
  (Object.values(WorkflowStepType) as string[]).includes(stepType)
    ? stepType as WorkflowStepType
    : WorkflowStepType.APP_BASED;

const cloneFieldsResponse = (fields: FieldsResponse): FieldsResponse => ({
  requestFields: [...(fields.requestFields || [])],
  responseFields: [...(fields.responseFields || [])]
});

export const EditWorkflowV2 = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { workflowId } = useParams();
  const { token } = useSelector((state: RootState) => state.user)
  const { applications } = useSelector((state: RootState) => state.application)
  const { workflows, create } = useSelector((state: RootState) => state.workflow)
  const [workflowDetails, setWorkflowDetails] = useState<WorkflowInDetailV2 | undefined>(undefined)
  const selectedWorkflow = workflows.find(w => w.id === workflowId)

  const form = useForm<z.infer<typeof WorkflowCreateUpdateSchemaV2>>({
    resolver: zodResolver(WorkflowCreateUpdateSchemaV2),
    defaultValues: {
      nickname: '',
    },
  })
  const formProps = getFormProps(form, WorkflowCreateUpdateSchemaV2)

  useEffect(() => {
    if (selectedWorkflow && token && !workflowDetails && applications?.length) {
      dispatch(getSingleWorkflow(token, selectedWorkflow.id))
        .then(async res => {
          if (res.data?.workflow) {
            setWorkflowDetails(res.data.workflow)
            form.setValue('nickname', res.data.workflow.nickname)
            // Run Type and CRON
            dispatch(workflowActions.setWorkflowRunType(res.data.workflow?.startingFrom ? WorkflowRunType.SCHEDULED : WorkflowRunType.MANUAL))
            if (res.data.workflow?.workflowSchedule) {
              // Try to decode original cron from metadata
              const metadata = decodeCronMetadata(res.data.workflow.workflowSchedule.scheduleType)

              if (metadata) {
                // Primary path: Use original cron from metadata for WYSIWYG
                dispatch(workflowActions.setCron(metadata.originalCron))
                dispatch(workflowActions.setTimezone(metadata.originalTimezone))
              } else {
                // Fallback: Convert UTC back to timezone for old workflows (created before metadata implementation)
                dispatch(workflowActions.setCron(
                  convertUTCToQuartzCron(
                    res.data.workflow.workflowSchedule.cronExpression,
                    res.data.workflow.workflowSchedule.timezone
                  )
                ))
                dispatch(workflowActions.setTimezone(res.data.workflow.workflowSchedule.timezone))
              }
            }
            if (res.data.workflow.startingFrom && res.data.workflow?.workflowSchedule?.timezone) {
              const parsedDate = convertFromUTC(res.data.workflow.startingFrom, res.data.workflow.workflowSchedule.timezone)
              dispatch(workflowActions.setExecutionStartDate(parsedDate.dateString))
              dispatch(workflowActions.setExecutionHour(parsedDate.hour))
              dispatch(workflowActions.setExecutionMinute(parsedDate.minute))
              dispatch(workflowActions.setAmPm(parsedDate.period))
            }
            // Tasks/Steps
            const sortedWorkflowTasks = [...res.data.workflow.workflowTasks].sort((a, b) => a.order - b.order);
            const transformationFieldsByOrder = new Map<number, FieldsResponse>();

            const mappedSteps = sortedWorkflowTasks.map(t => {
              const resolvedAppId = applications.find(app => app.name === t.app.name)?.id || t.app.id || '';
              const credentialId = t.workflowTaskCredentials[0]?.credentialId ?? '';
              const stepType = normalizeStepType(t.stepType);

              let availableFields: FieldsResponse | undefined = undefined;

              if (stepType === WorkflowStepType.TRANSFORMATION) {
                const selectedTransformation = t.transformationSteps?.find(ts => ts.stepName === t.action) || t.transformationSteps?.[0];

                if (selectedTransformation) {
                  const config = selectedTransformation.configuration as TransformationConfigurationInput;
                  const previousFields = transformationFieldsByOrder.get(t.order - 1);
                  availableFields = buildTransformationSchema(
                    config,
                    selectedTransformation.stepType,
                    previousFields
                  );
                } else {
                  availableFields = cloneFieldsResponse(ADD_CUSTOM_LABEL_FIELD_RESPONSE);
                }

                if (availableFields) {
                  transformationFieldsByOrder.set(t.order, availableFields);
                }
              }

              return {
                id: resolvedAppId,
                app: t.app,
                stepType,
                credentialId,
                action: t.action,
                subject: t.subject,
                filters: t.workflowTaskFilters,
                options: t.workflowTaskOptions,
                order: t.order,
                requiredOptions: [],
                availableFields: availableFields ? cloneFieldsResponse(availableFields) : undefined
              };
            });

            dispatch(workflowActions.setSteps(mappedSteps))
            // set Transformation Configurations
            const transformationSteps = res.data.workflow.workflowTasks
              .filter(task => task.stepType === WorkflowStepType.TRANSFORMATION)
              .flatMap(task => (task.transformationSteps || []).map(ts => ({
                taskOrder: task.order,
                stepName: ts.stepName,
                stepType: (ts.stepType ?? '') as TransformationStepType,
                configuration: ts.configuration,
                stepId: ts.id,
                taskId: ts.workflowTaskId,
                isActive: ts.isEnabled,
                validationRules: ts.validationRules
              })))
            dispatch(workflowActions.setTransformationConfigurations(transformationSteps))
            // Set Field Mappings
            const workflowFields = res.data.workflow.workflowFields;
            const workflowFieldMetaData = res.data.workflow.workflowFieldMetaData || [];
            const groupedMappings: FieldMappingType[] = Object.values(
              workflowFields.reduce<Record<string, FieldMappingType>>((acc, {
                sourceField,
                destinationField,
                sourceTaskId,
                destinationTaskId,
                order,
                options
              }) => {
                // find sourceApp and destinationApp
                const sourceStep = res.data?.workflow.workflowTasks.find(st => st.id === sourceTaskId)
                const destinationStep = res.data?.workflow.workflowTasks.find(st => st.id === destinationTaskId)

                const metaData = workflowFieldMetaData.find(meta =>
                  meta.destinationTaskId === destinationTaskId &&
                  meta.sourceTaskId === sourceTaskId
                )
                const key = metaData?.iterateThrough
                  ? `${destinationStep?.order};${destinationStep?.appId}::${metaData.iterateThrough}`
                  : `${destinationStep?.order};${destinationStep?.appId}`

                if (!acc[key]) {
                  acc[key] = {
                    destinationId: `${destinationStep?.order};${destinationStep?.appId}`,
                    mappings: [],
                    iterateThrough: metaData?.iterateThrough || undefined
                  };
                }
                acc[key]?.mappings.push({
                  dataSourceFieldId: `${sourceStep?.order};${sourceStep?.app.name}=>${sourceField}`,
                  fieldName: destinationField,
                  order,
                  options: options !== null ? options : undefined,
                });

                return acc;
              }, {})
            );
            dispatch(workflowActions.setFieldMappings(groupedMappings))
          }
        })
    }

  }, [token, selectedWorkflow, workflowDetails, applications, dispatch, form])

  const onEditWorkflowFormSubmit = async (
    values: z.infer<typeof WorkflowCreateUpdateSchemaV2>
  ) => {
    if (!token || !workflowId || !workflowDetails) return

    const isDraft = workflowDetails.status === WorkflowStatus.PENDING

    const builtWorkflowTasks = buildUpdatedWorkflowTasks(workflowDetails?.workflowTasks as WorkflowTask[], create.steps)
    const activeTaskIds = Array.from(new Set(
      builtWorkflowTasks
        .filter(task => !task.remove && task.id)
        .map(task => task.id as string)
    ))

    const { workflowFields: workflowFieldDiff, workflowFieldMetadata: workflowFieldMetadataDiff } =
      getFieldMappingsAndMetadataDifference(
        workflowDetails.workflowFields,
        workflowDetails.workflowFieldMetaData ?? [],
        create.fieldMappings,
        applications,
        builtWorkflowTasks
      )

    const builtWorkflowFields = workflowFieldDiff.filter(field => {
      if (!field.destinationTaskId) return true;
      return activeTaskIds.includes(field.destinationTaskId);
    })

    const groupedWorkflowFields = groupWorkflowFieldDiffs(builtWorkflowFields)

    const builtWorkflowFieldMetadata = workflowFieldMetadataDiff.filter(meta => {
      if (meta.remove) return true;
      if (!meta.destinationTaskId) return true;
      return activeTaskIds.includes(meta.destinationTaskId);
    })
    const transformationStepDiff = getTransformationsDifference(
      workflowDetails.workflowTasks,
      create.transformationConfigurations,
      builtWorkflowTasks
    )

    const workflowUpdateRequestBody = {
      nickname: values.nickname,
      startingFrom: create.runType === WorkflowRunType.SCHEDULED ?
        convertToUTC(
          create.executionStartDate,
          create.executionStartHour,
          create.executionStartMinute,
          create.amPm,
          create.timezone
        ) : null,
      workflowSchedule: create.runType === WorkflowRunType.SCHEDULED ? {
        // Store original cron in scheduleType as metadata for WYSIWYG display
        id: workflowDetails?.workflowSchedule?.id,
        scheduleType: encodeCronMetadata(create.cron, create.timezone),
        timezone: create.timezone,
        cronExpression: convertQuartzCronToUTC(create.cron, create.timezone)
      } : null,
      workflowTasks: builtWorkflowTasks,
      // workflow fields & metadata
      workflowFields: groupedWorkflowFields,
      workflowFieldMetadata: builtWorkflowFieldMetadata,
      transformationSteps: transformationStepDiff
    }

    // Handle draft workflow updates (no validation)
    if (isDraft) {
      const resp = await dispatch(updateDraftWorkflow(token, workflowId, workflowUpdateRequestBody))
      if (resp.data) {
        form.reset({
          nickname: '',
        })
        return navigate('/workflows')
      }
    }

    // Handle published workflow updates (with validation)
    const tasksForValidation = builtWorkflowTasks
      .filter(task => !task.remove)
      .map(task => ({
        appId: task.appId,
        action: task.action,
        subject: task.subject,
        credentialIds: task.workflowTaskCredentials.map(c => c.credentialId),
        order: task.order,
        filters: task.workflowTaskFilters.map(f => ({ filter: f.filter, filterValue: f.filterValue })),
        options: task.workflowTaskOptions.map(o => ({ option: o.option, optionValue: o.optionValue })),
        stepType: task.stepType
      }))

    if (
      isValidActionsSubjectsAndCredentials(tasksForValidation, applications) &&
      hasValidApplications(tasksForValidation, applications)
    ) {
      const resp = await dispatch(updateWorkflow(token, workflowId, workflowUpdateRequestBody))
      if (resp.data) {
        form.reset({
          nickname: '',
        })
        navigate('/workflows')
      }
    }
  }

  return (
    <div className="protectedPageLayout">
      <BackButton buttonLink="/workflows" buttonText="Back to Workflows" />
      <PageTitle title={`Edit ${selectedWorkflow?.nickname ?? ''}${workflowDetails?.status === WorkflowStatus.PENDING ? ' (Draft)' : ''}`} />
      <Form {...form}>
        <form
          id="edit-workflow"
          onSubmit={form.handleSubmit(onEditWorkflowFormSubmit)}
          className="space-y-8 flex flex-col items-start"
        >
          <Section
            title=""
            subtitle="Workflow Configuration"
            description="Name your workflow and choose when your workflow gets triggered"
          >
            {/* NickName */}
            <InputFieldWrapper title="Give your workflow a nickname">
              <TextInputField
                name="nickname"
                label=""
                placeholder="Nickname"
                error={!!form.formState.errors.nickname}
                additionalClassName="w-full bg-background placeholderText"
                {...formProps}
              />
            </InputFieldWrapper>
            <div className="my-8" />
            {/* Workflow Execution */}
            <InputFieldWrapper title="How does your workflow start?">
              <WorkflowExecution />
            </InputFieldWrapper>
            <div className="my-8" />
          </Section>
          {create.steps.map((taskStep, i) => {
            const app = applications.find(app => app.id === taskStep.id)
            if (app) {
              return (
                <StepTask
                  key={i + app.id}
                  index={i}
                  wfStep={taskStep}
                />
              )
            } else if (!app && taskStep?.stepType === WorkflowStepType.TRANSFORMATION) {
              return (
                <StepTransformation
                  key={i}
                  index={i}
                  wfStep={taskStep}
                />
              )
            }
          })}
          <div className="w-full flex flex-col">
            <div className="w-full flex flex-row items-center justify-end">
              <SelectTaskApplication applications={applications} />
              <div className="mr-4" />
              <Button
                type="button"
                className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                variant={ButtonVariant.OUTLINE}
                onClick={() => {
                  const orders = create.steps
                    .map(s => s.order)
                    .filter((n): n is number => Number.isFinite(n));

                  // next order: 0 if none, else max + 1
                  const nextOrder = orders.length ? Math.max(...orders) + 1 : 0;

                  dispatch(workflowActions.addStep({
                    id: `${WorkflowStepType.TRANSFORMATION}${nextOrder}`,
                    stepType: WorkflowStepType.TRANSFORMATION,
                    action: '',
                    subject: 'data',
                    credentialId: '',
                    filters: [],
                    options: [],
                    order: nextOrder,
                    requiredOptions: []
                  }));
                }}
              >
                <Settings className="mr-2" size={16} />
                Add Transformation
              </Button>
            </div>
            <Separator className="my-6" />
            <div className="text-right">
              <Button
                type='submit'
                disabled={false}
                variant={ButtonVariant.DEFAULT}
                className='font-semibold'
              >
                Update Workflow
                {/* TODO Uncomment the below line when working from hs-draft-wf-startover branch */}
                {/* {workflowDetails?.status === WorkflowStatus.PENDING ? 'Update Draft Workflow' : 'Update Workflow'} */}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
